import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  CircularProgress, 
  Alert,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  BarElement,
  Title, 
  Tooltip, 
  Legend,
  ArcElement
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { getTestExecutions } from '../../services/testService';
import { getAllFixVersions } from '../../services/apiService';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  ChartDataLabels
);

/**
 * ReleaseHealthCharts component for displaying comparison charts for releases
 * @returns {JSX.Element} ReleaseHealthCharts component
 */
const ReleaseHealthCharts = () => {
  // State for releases and test data
  const [releases, setReleases] = useState([]);
  const [testData, setTestData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Fetch releases and test data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch all releases
        const releasesResponse = await getAllFixVersions();
        
        let releasesData = [];
        
        // Process releases response based on format
        if (releasesResponse && releasesResponse.data && Array.isArray(releasesResponse.data)) {
          releasesData = releasesResponse.data;
        } else if (releasesResponse && Array.isArray(releasesResponse)) {
          releasesData = releasesResponse;
        } else if (releasesResponse && releasesResponse.data && releasesResponse.data.data && Array.isArray(releasesResponse.data.data)) {
          releasesData = releasesResponse.data.data;
        } else if (releasesResponse && releasesResponse.success && Array.isArray(releasesResponse.data)) {
          releasesData = releasesResponse.data;
        } else {
          throw new Error('Invalid releases response format');
        }
        
        // Filter active releases and sort by version
        const activeReleases = releasesData
          .filter(release => release.active)
          .sort((a, b) => {
            // Parse version strings like 5.9.08
            const versionA = a.version.split('.').map(Number);
            const versionB = b.version.split('.').map(Number);
            
            // Compare major version
            if (versionA[0] !== versionB[0]) return versionA[0] - versionB[0];
            // Compare minor version
            if (versionA[1] !== versionB[1]) return versionA[1] - versionB[1];
            // Compare patch version
            return versionA[2] - versionB[2];
          });
        
        // Get the last 10 releases
        const last10Releases = activeReleases.slice(-10);
        
        setReleases(last10Releases);
        
        // Fetch test data for each release
        const testDataMap = {};
        
        for (const release of last10Releases) {
          try {
            const testResponse = await getTestExecutions(release.version);
            
            if (testResponse && testResponse.data) {
              testDataMap[release.version] = testResponse.data;
            }
          } catch (err) {
            console.error(`Error fetching test data for release ${release.version}:`, err);
            // Continue with other releases even if one fails
          }
        }
        
        setTestData(testDataMap);
      } catch (err) {
        console.error('Error fetching data for comparison charts:', err);
        setError('Failed to load data for comparison charts. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Prepare chart data for pass percentage
  const preparePassPercentageData = () => {
    const labels = releases.map(release => release.version);
    const data = releases.map(release => {
      const tests = testData[release.version] || [];
      const executedTests = tests.filter(test => 
        test.outcome === 'Passed' || test.outcome === 'Failed'
      );
      const passedTests = tests.filter(test => test.outcome === 'Passed');
      
      return executedTests.length > 0 
        ? (passedTests.length / executedTests.length) * 100 
        : 0;
    });
    
    return {
      labels,
      datasets: [
        {
          label: 'Pass Percentage',
          data,
          borderColor: '#4caf50',
          backgroundColor: 'rgba(76, 175, 80, 0.2)',
          borderWidth: 2,
          fill: true,
          tension: 0.4
        }
      ]
    };
  };
  
  // Prepare chart data for automation test cases
  const prepareAutomationTestsData = () => {
    const labels = releases.map(release => release.version);
    
    // Get all possible outcomes
    const allTests = Object.values(testData).flat();
    const uniqueOutcomes = [...new Set(allTests.map(test => test.outcome))].filter(outcome => outcome);
    
    // Create datasets for each outcome
    const datasets = uniqueOutcomes.map(outcome => {
      let color;
      switch(outcome) {
        case 'Passed':
          color = '#4caf50'; // Green
          break;
        case 'Failed':
          color = '#f44336'; // Red
          break;
        case 'Blocked':
          color = '#ff9800'; // Orange
          break;
        case 'Not Applicable':
          color = '#9e9e9e'; // Gray
          break;
        case 'Not Executed':
          color = '#2196f3'; // Blue
          break;
        default:
          color = '#9c27b0'; // Purple
      }
      
      const data = releases.map(release => {
        const tests = testData[release.version] || [];
        return tests.filter(test => 
          test.outcome === outcome && 
          (test.automationStatus === 'Automated-UI' || test.automationStatus === 'Automated-API')
        ).length;
      });
      
      return {
        label: outcome,
        data,
        backgroundColor: color,
        borderColor: color,
        borderWidth: 1
      };
    });
    
    return {
      labels,
      datasets
    };
  };
  
  // Prepare chart data for overall test cases
  const prepareOverallTestsData = () => {
    const labels = releases.map(release => release.version);
    
    // Get all possible outcomes
    const allTests = Object.values(testData).flat();
    const uniqueOutcomes = [...new Set(allTests.map(test => test.outcome))].filter(outcome => outcome);
    
    // Create datasets for each outcome
    const datasets = uniqueOutcomes.map(outcome => {
      let color;
      switch(outcome) {
        case 'Passed':
          color = '#4caf50'; // Green
          break;
        case 'Failed':
          color = '#f44336'; // Red
          break;
        case 'Blocked':
          color = '#ff9800'; // Orange
          break;
        case 'Not Applicable':
          color = '#9e9e9e'; // Gray
          break;
        case 'Not Executed':
          color = '#2196f3'; // Blue
          break;
        default:
          color = '#9c27b0'; // Purple
      }
      
      const data = releases.map(release => {
        const tests = testData[release.version] || [];
        return tests.filter(test => test.outcome === outcome).length;
      });
      
      return {
        label: outcome,
        data,
        backgroundColor: color,
        borderColor: color,
        borderWidth: 1
      };
    });
    
    return {
      labels,
      datasets
    };
  };
  
  // Chart options
  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${context.raw.toFixed(1)}%`;
          }
        }
      },
      datalabels: {
        color: '#000',
        anchor: 'end',
        align: 'top',
        formatter: (value) => value.toFixed(1) + '%',
        font: {
          weight: 'bold',
          size: 12
        },
        padding: 6
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: 'Percentage (%)'
        }
      }
    }
  };
  
  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      datalabels: {
        color: '#fff',
        anchor: 'center',
        align: 'center',
        formatter: (value) => value > 0 ? value : '',
        font: {
          weight: 'bold',
          size: 12
        }
      }
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
        beginAtZero: true,
        title: {
          display: true,
          text: 'Number of Tests'
        }
      }
    }
  };
  
  // Check if there are any releases available
  const hasReleases = releases.length > 0;
  
  // If no data is available, show a message
  const noDataForTab = !hasReleases;
  
  return (
    <Box>
      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : Object.keys(testData).length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No test data available for comparison. Please fetch test data for at least one release first.
        </Alert>
      ) : (
        <>
          {noDataForTab ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              No releases available for comparison.
            </Alert>
          ) : (
            <Grid container spacing={3}>
              {/* Pass Percentage Row */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* Description (Column 1 - 20%) */}
                <Grid item xs={12} md={2}>
                  <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" gutterBottom>
                      Pass Percentage
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                      Shows the percentage of tests that passed across different releases. A higher percentage indicates better test execution success rate.
                    </Typography>
                  </Paper>
                </Grid>
                
                {/* Chart (Column 2 - 40%) */}
                <Grid item xs={12} md={5}>
                  <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" gutterBottom>
                      Pass Percentage
                    </Typography>
                    <Box sx={{ height: '300px', width: '100%', position: 'relative' }}>
                      <Line 
                        data={preparePassPercentageData()} 
                        options={{
                          ...lineOptions,
                          maintainAspectRatio: false,
                          responsive: true,
                          plugins: {
                            ...lineOptions.plugins,
                            datalabels: {
                              display: false
                            }
                          }
                        }} 
                      />
                    </Box>
                  </Paper>
                </Grid>
                
                {/* Table (Column 3 - 40%) */}
                <Grid item xs={12} md={5}>
                  <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" gutterBottom>
                      Pass Percentage
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Release</TableCell>
                            <TableCell align="right">Pass Percentage</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {releases.map((release, index) => {
                            const chartData = preparePassPercentageData();
                            const value = chartData.datasets[0].data[index];
                            return (
                              <TableRow key={release.version}>
                                <TableCell>{release.version}</TableCell>
                                <TableCell align="right">{value.toFixed(1)}%</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>
              </Grid>
              
              {/* Automated Tests Row */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* Description (Column 1 - 20%) */}
                <Grid item xs={12} md={2}>
                  <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" gutterBottom>
                      Automated Tests
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                      Displays the breakdown of automated test results by status (Passed, Failed, Not Run, etc.) for each release. Helps track automation coverage and execution quality.
                    </Typography>
                  </Paper>
                </Grid>
                
                {/* Chart (Column 2 - 40%) */}
                <Grid item xs={12} md={5}>
                  <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" gutterBottom>
                      Automated Tests
                    </Typography>
                    <Box sx={{ height: '300px', width: '100%', position: 'relative' }}>
                      <Bar 
                        data={prepareAutomationTestsData()} 
                        options={{
                          ...barOptions,
                          maintainAspectRatio: false,
                          responsive: true,
                          plugins: {
                            ...barOptions.plugins,
                            datalabels: {
                              display: false
                            }
                          }
                        }} 
                      />
                    </Box>
                  </Paper>
                </Grid>
                
                {/* Table (Column 3 - 40%) */}
                <Grid item xs={12} md={5}>
                  <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" gutterBottom>
                      Automated Tests
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Release</TableCell>
                            {prepareAutomationTestsData().datasets.map(dataset => (
                              <TableCell key={dataset.label} align="right">{dataset.label}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {releases.map((release, releaseIndex) => (
                            <TableRow key={release.version}>
                              <TableCell>{release.version}</TableCell>
                              {prepareAutomationTestsData().datasets.map((dataset, datasetIndex) => (
                                <TableCell key={`${release.version}-${dataset.label}`} align="right">
                                  {dataset.data[releaseIndex]}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>
              </Grid>
              
              {/* Total Tests Row */}
              <Grid container spacing={3}>
                {/* Description (Column 1 - 20%) */}
                <Grid item xs={12} md={2}>
                  <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" gutterBottom>
                      Total Tests
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                      Represents the complete test execution summary including all test types (automated and manual) across releases. Shows overall testing volume and outcomes.
                    </Typography>
                  </Paper>
                </Grid>
                
                {/* Chart (Column 2 - 40%) */}
                <Grid item xs={12} md={5}>
                  <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" gutterBottom>
                      Total Tests
                    </Typography>
                    <Box sx={{ height: '300px', width: '100%', position: 'relative' }}>
                      <Bar 
                        data={prepareOverallTestsData()} 
                        options={{
                          ...barOptions,
                          maintainAspectRatio: false,
                          responsive: true,
                          plugins: {
                            ...barOptions.plugins,
                            datalabels: {
                              display: false
                            }
                          }
                        }} 
                      />
                    </Box>
                  </Paper>
                </Grid>
                
                {/* Table (Column 3 - 40%) */}
                <Grid item xs={12} md={5}>
                  <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" gutterBottom>
                      Total Tests
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Release</TableCell>
                            {prepareOverallTestsData().datasets.map(dataset => (
                              <TableCell key={dataset.label} align="right">{dataset.label}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {releases.map((release, releaseIndex) => (
                            <TableRow key={release.version}>
                              <TableCell>{release.version}</TableCell>
                              {prepareOverallTestsData().datasets.map((dataset, datasetIndex) => (
                                <TableCell key={`${release.version}-${dataset.label}`} align="right">
                                  {dataset.data[releaseIndex]}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          )}
        </>
      )}
    </Box>
  );
};

export default ReleaseHealthCharts;
